import React from 'react';
import { Form, Button, Row, Col, Card } from 'react-bootstrap';
import { Formik, FieldArray } from 'formik';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';

import AddressForm from '../AddressForm';
import SelectField from '../../form/SelectField';
import CheckboxesField from '../../form/CheckboxesField';
import LabelField from '../../form/LabelField';
import { isRequiredField } from '../../form/RequiredAsterisk';
import ScrollToFirstError from '../../route/ScrollToFirstError';

const ProfileFormStep3 = ({ schema, onSubmit, onBack, initialValues, dataTypes, isLoading, isEdit }) => {
    const { questionPhrasing } = dataTypes;
    const enabledFields = dataTypes.enabledFields || [];
    const { t } = useTranslation();

    return (
        <Formik
            validationSchema={schema}
            onSubmit={onSubmit}
            initialValues={initialValues}
            enableReinitialize={true}
            validateOnChange={false}
            validateOnBlur={false}
        >
            {({ handleSubmit, handleChange, values, errors, setFieldValue }) => (
                <Col md={6} sm={12} className="mx-auto">
                    <Form noValidate onSubmit={handleSubmit} className="card shadow-sm rounded p-4">
                        <h4 className="text-center">{t('intake.step3Heading')}</h4>
                        <p className="text-center text-muted mb-4">
                            {t('intake.step3Of3')}
                        </p>

                        {['addressLine1', 'addressLine2', 'city', 'county', 'state', 'zipCode'].some((field) =>
                            enabledFields.includes(field)
                        ) && (
                            <Card className="mb-4">
                                <Card.Header className="text-center font-weight-bold">
                                    {t('address.heading')}
                                </Card.Header>
                                <Card.Body>
                                    <AddressForm
                                        values={values}
                                        handleChange={handleChange}
                                        setFieldValue={setFieldValue}
                                        errors={errors}
                                        schema={schema}
                                    />

                                    {enabledFields.includes('housingType') && (
                                        <Form.Group controlId="housingType">
                                            <LabelField
                                                label={questionPhrasing.housingType || t('intake.housingType')}
                                                isRequired={isRequiredField(schema, 'housingType')}
                                            />
                                            <SelectField
                                                as="select"
                                                name="housingType"
                                                value={values.housingType}
                                                isInvalid={!!errors.housingType}
                                                options={dataTypes.housingTypes}
                                                includeEmpty={true}
                                            />
                                            <Form.Control.Feedback type="invalid">{errors.housingType}</Form.Control.Feedback>
                                        </Form.Group>
                                    )}
                                </Card.Body>
                            </Card>
                        )}

                        {enabledFields.includes('socialProgramTypes') && (
                            <CheckboxesField
                                schema={schema}
                                types={dataTypes.socialProgramTypes}
                                name="socialProgramTypes"
                                value={values.socialProgramTypes}
                                onlyOneWhen={['declined_to_answer', 'do_not_know', 'no_benefits', 'other_benefits']}
                                label={questionPhrasing.socialPrograms || t('intake.socialPrograms')}
                                isRequired={isRequiredField(schema, 'socialProgramTypes')}
                            />
                        )}

                        {enabledFields.includes('dietaryConsiderationTypes') && (
                            <CheckboxesField
                                schema={schema}
                                types={dataTypes.dietaryConsiderationTypes}
                                name="dietaryConsiderationTypes"
                                value={values.dietaryConsiderationTypes}
                                label={
                                    questionPhrasing.dietaryConsiderationTypes ||
                                    t('intake.dietaryConsiderationTypes')
                                }
                                isRequired={isRequiredField(schema, 'dietaryConsiderationTypes')}
                            />
                        )}

                        {enabledFields.includes('referredBy') && (
                            <Form.Group controlId="referredBy">
                                <LabelField
                                    label={questionPhrasing.referredBy || t('intake.referredBy')}
                                    isRequired={isRequiredField(schema, 'referredBy')}
                                />
                                <SelectField
                                    as="select"
                                    name="referredBy"
                                    value={values.referredBy}
                                    isInvalid={!!errors.referredBy}
                                    options={dataTypes.referredBy}
                                    includeEmpty={true}
                                />
                                <Form.Control.Feedback type="invalid">{errors.referredBy}</Form.Control.Feedback>
                            </Form.Group>
                        )}

                        {enabledFields.includes('languageTypes') && (
                            <Form.Group controlId="languageTypes">
                                <LabelField
                                    label={questionPhrasing.languageTypes || t('intake.languageTypes')}
                                    isRequired={isRequiredField(schema, 'languageTypes')}
                                />
                                <Select
                                    options={dataTypes.languageTypes.map((type) => ({
                                        label: type.label,
                                        value: type.name,
                                    }))}
                                    isMulti
                                    value={values.languageTypes?.map((type) => ({ label: type, value: type }))}
                                    required={isRequiredField(schema, 'languageTypes')}
                                    name="languageTypes"
                                    onChange={(e) => {
                                        setFieldValue(
                                            'languageTypes',
                                            e?.map((opt) => opt.value)
                                        );
                                    }}
                                    className={!!errors.languageTypes ? 'is-invalid invalid-language' : ''}
                                />
                                <Form.Control.Feedback type="invalid">{errors.languageTypes}</Form.Control.Feedback>
                            </Form.Group>
                        )}

                        {(enabledFields.includes('incomeType') || enabledFields.includes('income')) && (
                            <Form.Group controlId="incomeType">
                                <Row>
                                    {enabledFields.includes('incomeType') && (
                                        <Col md="6">
                                            <LabelField
                                                label={t('intake.incomeType')}
                                                isRequired={isRequiredField(schema, 'incomeType')}
                                            />
                                            <SelectField
                                                as="select"
                                                name="incomeType"
                                                value={values.incomeType}
                                                isInvalid={!!errors.incomeType}
                                                options={dataTypes.income_sourceTypes}
                                                includeEmpty={true}
                                            />
                                            <Form.Control.Feedback type="invalid">{errors.incomeType}</Form.Control.Feedback>
                                        </Col>
                                    )}
                                    {enabledFields.includes('income') && (
                                        <Col md="6">
                                            <LabelField
                                                label={t('intake.incomeAmount')}
                                                isRequired={isRequiredField(schema, 'incomeAmount')}
                                            />
                                            <Form.Control
                                                type="number"
                                                name="incomeAmount"
                                                value={values.incomeAmount}
                                                placeholder={t('intake.incomeAmount')}
                                                onChange={handleChange}
                                                isInvalid={!!errors.incomeAmount}
                                            />
                                            <Form.Control.Feedback type="invalid">{errors.incomeAmount}</Form.Control.Feedback>
                                        </Col>
                                    )}
                                </Row>
                            </Form.Group>
                        )}

                        {enabledFields.includes('otherIncomes') && (
                            <CheckboxesField
                                schema={schema}
                                types={dataTypes.income_sourceTypes}
                                name="otherIncomes"
                                value={values.otherIncomes}
                                label={questionPhrasing.otherIncomes || t('intake.otherIncomes')}
                            />
                        )}

                        {enabledFields.includes('expenseTypes') && (
                            <Card className="mb-4">
                                <Card.Header className="text-center font-weight-bold">{t('intake.expenses')}</Card.Header>
                                <Card.Body>
                                    <FieldArray
                                        name="expenseTypes"
                                        render={(arrayHelpers) => (
                                            <>
                                                {values.expenseTypes && values.expenseTypes.length > 0 &&
                                                    values.expenseTypes.map((type, index) => (
                                                        <Row key={`expense-${index}`}>
                                                            <Form.Group as={Col} md="6" controlId={`expenseTypes.${index}.type`}>
                                                                <LabelField
                                                                    label={questionPhrasing.expenses || t('intake.expenses')}
                                                                    isRequired={isRequiredField(schema, 'expenseTypes')}
                                                                />
                                                                <SelectField
                                                                    as="select"
                                                                    name={`expenseTypes.${index}.type`}
                                                                    isInvalid={
                                                                        errors.expenseTypes &&
                                                                        errors.expenseTypes[index] &&
                                                                        !!errors.expenseTypes[index].type
                                                                    }
                                                                    options={dataTypes.expenseTypes}
                                                                    includeEmpty={true}
                                                                />
                                                                <Form.Control.Feedback type="invalid">
                                                                    {errors.expenseTypes &&
                                                                        errors.expenseTypes[index] &&
                                                                        errors.expenseTypes[index].type}
                                                                </Form.Control.Feedback>
                                                            </Form.Group>

                                                            <Form.Group as={Col} md="5" controlId={`expenseTypes.${index}.amount`}>
                                                                <LabelField
                                                                    label={t('intake.expenseAmount')}
                                                                    isRequired={isRequiredField(schema, 'expenseTypes')}
                                                                />
                                                                <Form.Control
                                                                    type="number"
                                                                    name={`expenseTypes.${index}.amount`}
                                                                    placeholder={t('intake.expenseAmount')}
                                                                    onChange={handleChange}
                                                                    value={type.amount || ''}
                                                                    isInvalid={
                                                                        errors.expenseTypes &&
                                                                        errors.expenseTypes[index] &&
                                                                        !!errors.expenseTypes[index].amount
                                                                    }
                                                                />
                                                                <Form.Control.Feedback type="invalid">
                                                                    {errors.expenseTypes &&
                                                                        errors.expenseTypes[index] &&
                                                                        errors.expenseTypes[index].amount}
                                                                </Form.Control.Feedback>
                                                            </Form.Group>

                                                            <Form.Group as={Col} md="1" className="d-flex align-items-end pb-2">
                                                                <Button
                                                                    type="button"
                                                                    variant="danger"
                                                                    size="sm"
                                                                    onClick={() => {
                                                                        arrayHelpers.remove(index);
                                                                        setFieldValue('expenseCount', values.expenseCount - 1);
                                                                    }}
                                                                >
                                                                    <i className="fa fa-trash-o fa-lg"></i>
                                                                </Button>
                                                            </Form.Group>
                                                        </Row>
                                                    ))}

                                                <Button
                                                    type="button"
                                                    variant="link"
                                                    className="pl-0"
                                                    onClick={() => {
                                                        arrayHelpers.push({ type: '', amount: '' });
                                                        setFieldValue('expenseCount', values.expenseCount + 1);
                                                    }}
                                                >
                                                    + {t('intake.addExpense')}
                                                </Button>
                                            </>
                                        )}
                                    />
                                </Card.Body>
                            </Card>
                        )}

                        <Row>
                            <Col md={6} className="mb-2 mb-md-0">
                                <Button
                                    type="button"
                                    variant="outline-secondary"
                                    className="w-100"
                                    onClick={() => onBack(values)}
                                >
                                    {t('core.action.back')}
                                </Button>
                            </Col>
                            <Col md={6}>
                                <Button type="submit" variant="primary" className="w-100" disabled={isLoading}>
                                    {isLoading
                                        ? t('core.action.loading')
                                        : t('core.action.review')}
                                </Button>
                            </Col>
                        </Row>
                        <ScrollToFirstError />
                    </Form>
                    {isEdit && (
                        <p className="text-center mt-3 mb-0">
                            <Link to="/dashboard">{t('dashboard.return')}</Link>
                        </p>
                    )}
                </Col>
            )}
        </Formik>
    );
};

export default ProfileFormStep3;